import INGREDIENT_DB from "../data/ingredients.json";

// ── Unit normalisation ────────────────────────────────────────────────────────
const UNIT_ALIASES = {
  tbsp: "tbsp", tablespoon: "tbsp", tablespoons: "tbsp",
  tsp: "tsp", teaspoon: "tsp", teaspoons: "tsp",
  cup: "cup", cups: "cup",
  g: "g", gram: "g", grams: "g",
  kg: "kg", kilogram: "kg", kilograms: "kg",
  ml: "ml", milliliter: "ml", milliliters: "ml", millilitre: "ml", millilitres: "ml",
  l: "l", liter: "l", litre: "l", liters: "l", litres: "l",
  oz: "oz", ounce: "oz", ounces: "oz",
  lb: "lb", lbs: "lb", pound: "lb", pounds: "lb",
};

const UNIT_KEYS = Object.keys(UNIT_ALIASES).join("|");
const LEADING_QTY_RE = new RegExp(`^([\\d]+(?:[./][\\d]+)?)\\s*(${UNIT_KEYS})\\.?\\s+`, "i");
const BARE_NUMBER_RE = /^([\d]+(?:[./][\d]+)?)\s+/;

function parseFraction(str) {
  return str.includes("/")
    ? str.split("/").reduce((a, b) => parseFloat(a) / parseFloat(b))
    : parseFloat(str);
}

/** Strip leading quantity + unit from a raw ingredient string, return { qty, unit, base }. */
function parseIngredient(raw) {
  const s = raw.trim();
  const unitMatch = s.match(LEADING_QTY_RE);
  if (unitMatch) {
    return {
      qty: parseFraction(unitMatch[1]),
      unit: UNIT_ALIASES[unitMatch[2].toLowerCase()] || unitMatch[2].toLowerCase(),
      base: s.slice(unitMatch[0].length).trim(),
      original: raw,
    };
  }
  const numMatch = s.match(BARE_NUMBER_RE);
  if (numMatch) {
    return { qty: parseFraction(numMatch[1]), unit: null, base: s.slice(numMatch[0].length).trim(), original: raw };
  }
  return { qty: null, unit: null, base: s.trim(), original: raw };
}

// ── Smart match ───────────────────────────────────────────────────────────────
// Build a flat lookup map once at module load: alias/name (lowercase) → ingredient entry
const _lookup = new Map();
INGREDIENT_DB.forEach((entry) => {
  _lookup.set(entry.name.toLowerCase(), entry);
  (entry.aliases || []).forEach((alias) => _lookup.set(alias.toLowerCase(), entry));
});

/**
 * matchIngredientToDatabase(userInput)
 * Cleans the input and returns the matching ingredient entry from the master
 * database, or null if no match is found.
 */
export function matchIngredientToDatabase(userInput) {
  if (!userInput || typeof userInput !== "string") return null;
  const { base } = parseIngredient(userInput);
  const clean = base.toLowerCase().trim();

  // 1. Exact match on name or alias
  if (_lookup.has(clean)) return _lookup.get(clean);

  // 2. Partial match — find an entry whose name or alias is contained in the input
  for (const [key, entry] of _lookup.entries()) {
    if (clean.includes(key) && key.length > 3) return entry;
  }

  // 3. Reverse partial — input base is contained in a name/alias
  for (const [key, entry] of _lookup.entries()) {
    if (key.includes(clean) && clean.length > 3) return entry;
  }

  return null;
}

// ── Merge ─────────────────────────────────────────────────────────────────────
function formatMerged(qty, unit, displayName) {
  const rounded = Number.isInteger(qty) ? qty : Math.round(qty * 10) / 10;
  if (unit) return `${rounded} ${unit} ${displayName}`;
  return `${rounded} ${displayName}`;
}

/** Apply a numeric multiplier to a single raw ingredient string. */
export function applyIngredientMultiplier(raw, multiplier) {
  if (!raw || multiplier === 1) return raw;
  const { qty, unit, base } = parseIngredient(raw);
  if (qty === null) return raw;
  const newQty = qty * multiplier;
  const rounded = Number.isInteger(newQty) ? newQty : Math.ceil(newQty * 10) / 10;
  if (unit) return `${rounded} ${unit} ${base}`;
  return `${rounded} ${base}`;
}

/**
 * mergeShoppingIngredients(rawIngredients)
 *
 * Takes a flat array of raw ingredient strings from the plan and returns a
 * deduplicated, quantity-merged array.
 *
 * Deduplication key priority:
 *   1. ingredientId from the master database (via matchIngredientToDatabase)
 *   2. Normalised base name as fallback
 *
 * Merge rules:
 *   - Same key + same unit → quantities summed
 *   - Same key + different units → kept under same key, first occurrence wins for display
 */
export function mergeShoppingIngredients(rawIngredients) {
  // key → { qty, unit, displayName, matched }
  const map = new Map();

  rawIngredients.forEach((raw) => {
    if (!raw || typeof raw !== "string") return;
    const { qty, unit, base, original } = parseIngredient(raw);
    const matched = matchIngredientToDatabase(original);

    // Use ingredient ID as the deduplication key; fall back to normalised base name
    const key = matched ? matched.id : base.toLowerCase();
    const displayName = matched ? matched.name : base;

    if (!map.has(key)) {
      map.set(key, { qty, unit, displayName, matched, original });
      return;
    }

    const existing = map.get(key);

    // Merge quantities when both entries have one and share the same unit
    if (qty !== null && existing.qty !== null && existing.unit === unit) {
      existing.qty += qty;
    } else if (qty !== null && existing.qty === null) {
      existing.qty = qty;
      existing.unit = unit;
    }
    // Different units or no quantity on new item → silently ignore duplicate
  });

  return Array.from(map.values()).map(({ qty, unit, displayName }) => {
    if (qty === null) return displayName;
    return formatMerged(qty, unit, displayName);
  });
}
